import { formatTableData, getData } from '../utils/formatters';

interface KeyWestData {
  address?: string;
  parcelId?: string;
  ownerName?: string;
  ownerStreetAddress?: string;
  ownerCity?: string;
  ownerState?: string;
  ownerZip?: string;
  unit?: string;
  neighborhood?: string | number;
  subdivisionName?: string;
  section?: string | number;
  yearBuilt?: string | number;
  legalDescription?: string;
  keyName?: string;
  rpNumber?: string;
  zone?: string;
  zoningCode?: string;
  zoningLabel?: string;
  harcDistrict?: string;
  harcDistrictCode?: string;
  harcTrackingId?: string;
  harcContributing?: string;
  historicSiteName?: string;
  historicStyle?: string;
  historicSurveyDistrict?: string;
  historicSurveyEvaluation?: string;
  nationalRegisterListed?: string;
  historicYearBuilt?: string | number;
}

const KEY_WEST_ROWS = [
  {
    label: 'Address',
    getValue: (data: KeyWestData) => getData(data, 'address', 'No address available')
  },
  {
    label: 'Parcel ID',
    getValue: (data: KeyWestData) => getData(data, 'parcelId', 'No parcel ID available')
  },
  {
    label: 'Owner',
    getValue: (data: KeyWestData) => getData(data, 'ownerName', 'No owner information available')
  },
  {
    label: 'Owner Mailing Address',
    getValue: (data: KeyWestData) => {
      const street = data.ownerStreetAddress;
      const city = data.ownerCity;
      const state = data.ownerState;
      const zip = data.ownerZip;
      if (!street && !city && !state && !zip) {
        return 'No owner mailing address available';
      }
      const cityStateZip = [city, state].filter(Boolean).join(', ');
      return [street, [cityStateZip, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    }
  },
  {
    label: 'Zoning',
    getValue: (data: KeyWestData) => {
      if (data.zone) {
        return String(data.zone);
      }
      if (data.zoningCode && data.zoningLabel) {
        return `${data.zoningCode} - ${data.zoningLabel}`;
      }
      if (data.zoningCode) {
        return String(data.zoningCode);
      }
      return 'No zoning information available';
    }
  },
  {
    label: 'HARC District',
    // Absent when enrichment was skipped/failed, or when a contributing historic
    // site was found without a district polygon. Backend sets a definitive
    // "Not in a HARC district" only after both spatial queries come back empty.
    getValue: (data: KeyWestData) =>
      getData(data, 'harcDistrict', 'No HARC district information available')
  },
  {
    label: 'HARC Contributing',
    getValue: (data: KeyWestData) =>
      getData(data, 'harcContributing', 'No contributing-structure information available')
  },
  {
    label: 'Historic Site',
    getValue: (data: KeyWestData) => {
      const name = flattenCell(data.historicSiteName);
      const style = flattenCell(data.historicStyle);
      if (!name && !style) {
        return 'No historic site information available';
      }
      if (name && style) {
        return `${name} (${style})`;
      }
      return name || style || 'No historic site information available';
    }
  },
  {
    label: 'Year Built',
    getValue: (data: KeyWestData) => {
      const year = data.historicYearBuilt ?? data.yearBuilt;
      return year ? String(year) : 'No year built information available';
    }
  },
  {
    label: 'Legal Description',
    getValue: (data: KeyWestData) =>
      flattenCell(getData(data, 'legalDescription', 'No legal description available'))
  },
  {
    label: 'Neighborhood',
    getValue: (data: KeyWestData) =>
      flattenCell(getData(data, 'neighborhood', 'No neighborhood information available'))
  },
  {
    label: 'Subdivision',
    getValue: (data: KeyWestData) =>
      flattenCell(getData(data, 'subdivisionName', 'No subdivision information available'))
  }
];

const flattenCell = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value).replace(/\s+/g, ' ').trim();
};

export const formatKeyWestData = (data: KeyWestData): string => {
  return formatTableData(data, KEY_WEST_ROWS);
};
